<template>
  <FilterDropdown
    ref="filterDropdown"
    class="board-filter-popup"
    :hide-filter-popup="hideFilterPopup"
    :on-item-click="selectSearchResult"
    :on-item-right-side-click="selectSearchResult"
    :placeholder="translate(searchPlaceholder)"
    :search-results="searchResults"
    :selected-item-check="selectedBoardIndex"
    @query-change="handleQueryChange"
    @item-hover="handleItemHover"
    @esc="handleEsc"
  />
</template>

<script>
import FilterDropdown from '@/components/widgets/filters/FilterDropdown.vue';
import { SprintState } from '@superthread-com/api';
import useSprintStateIcon from '@/utilities/composables/useSprintStateIcon';

export default {
  name: 'BoardFilterDropdown',

  components: {
    FilterDropdown,
  },

  props: {
    selectedBoardId: {
      type: String,
      required: true,
    },
    projectIdFilter: {
      type: String,
      default: '',
    },
    searchPlaceholder: {
      type: String,
      default: 'searchForSpaceOrBoard',
    },
    showSprints: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['selectBoard', 'hidePopup', 'esc', 'item-hover'],

  setup() {
    const { sprintStateIcon } = useSprintStateIcon();

    return {
      sprintStateIcon,
    };
  },

  data() {
    return {
      searchValue: '',
    };
  },

  computed: {
    teamProjects() {
      return this.$store.getters.getTeamProjects || [];
    },

    boards() {
      return this.$store.getters.getFlatBoards;
    },

    searchResults() {
      if (!this.teamProjects.length) return [];

      const boardOptions = [];

      // loop throught flat boards object
      Object.keys(this.boards).forEach((boardId) => {
        const board = this.boards[boardId];
        const isSprint = board?.id.includes('s') ?? false;
        const sprintCompleted = [SprintState.Completed, SprintState.Canceled].includes(board.state);
        const sprintStatusText =
          board.state === SprintState.Active
            ? this.translate('activeSprintText')
            : this.translate('next');
        const projectId = this.projectIdFilter || board.project_id;
        const project = this.$store.getters.getTeamProjectById(projectId);
        if (
          !project ||
          project.id !== board.project_id ||
          (isSprint && sprintCompleted) ||
          (isSprint && !this.showSprints)
        )
          return;

        // create an array for FilterDropdownItem components
        boardOptions.push({
          id: board.id,
          label: isSprint ? `${board.title} (${sprintStatusText})` : board.title,
          checkbox: false,
          lighter: true,
          groupingItem: true,
          groupLabel: project?.title,
          groupIcon: project?.icon?.src || '',
          groupIconColor: project?.icon?.color || '',
          groupingItemId: project?.id,
          parentIcon: project?.icon || '',
          projectId: project?.id,
          icon: isSprint ? this.sprintStateIcon(board.state)?.icon : 'board',
          customIcon: true,
          customIconColor: isSprint
            ? this.sprintStateIcon(board?.state)?.color
            : 'var(--iconDefault)',
          groupIconType: project?.icon?.type,
          groupPrivateItem: project?.is_private,
        });
      });

      // sort boards by project id
      boardOptions.sort((a, b) => +a.projectId - +b.projectId);
      // add separator to the end of each project section
      boardOptions.forEach((board, index) => {
        const currentBoardProjectId = board.projectId;
        const nextBoardProjectId = boardOptions[index + 1]?.projectId;

        if (currentBoardProjectId !== nextBoardProjectId) {
          boardOptions[index].separator = true;
        }
      });

      delete boardOptions.at(-1)?.separator;

      if (!this.searchValue) {
        return boardOptions;
      }

      return boardOptions
        .filter(
          (board) =>
            board.label.toLowerCase().includes(this.searchValue.toLowerCase()) ||
            board.groupLabel.toLowerCase().includes(this.searchValue.toLocaleLowerCase())
        )
        .map((board, index, arr) => {
          // Fix separators to be before boards with project title
          const nextBoard = arr[index + 1];
          delete board.separator;
          if (nextBoard && board.groupingItemId !== nextBoard.groupingItemId) {
            board.separator = true;
          }
          return board;
        });
    },

    selectedBoardIndex() {
      return this.searchResults.findIndex((sr) => sr.id === this.selectedBoardId);
    },
  },

  mounted() {
    this.$store.dispatch('loadTeamProjects');
  },

  methods: {
    handleQueryChange({ value }) {
      this.searchValue = value;
    },

    selectSearchResult(board, index) {
      this.$emit('selectBoard', { board, index });
    },

    getSearchResults() {
      return this.searchResults;
    },

    hideFilterPopup(event) {
      this.$emit('hidePopup', event);
    },

    focusInput() {
      this.$nextTick(() => {
        this.$refs.filterDropdown.focusInput();
      });
    },

    handleEsc() {
      this.hideFilterPopup();
      this.$emit('esc');
    },

    handleItemHover(item) {
      this.$emit('item-hover', item);
    },
  },
};
</script>
